/**
 * Spreadsheet data for the regression family: named columns pasted from Excel, Google
 * Sheets, SPSS or a CSV file.
 *
 * Built on the same cell reader the reliability calculators use (`stats-research/matrix`),
 * so a tab, semicolon or comma separator, quoted cells, and `NA`/blank/`.` as missing all
 * behave identically across the site. What this adds:
 *
 * - **Header detection that survives text columns.** A regression data set often has a
 *   yes/no outcome or a group label, so "text in the first row" is not enough. The first
 *   row is a header when every cell in it is a word that does not reappear in its own
 *   column below.
 * - **Columns keep their raw text**, and a column is *numeric* only if every non-missing
 *   cell parses. A two-level text column can still be used as a 0/1 variable.
 * - **Complete-case selection**, so a model uses only the rows that have every variable
 *   it needs (listwise deletion), and reports how many were dropped.
 */

import { parseNumberInput } from "@/lib/parse-number";
import {
  type HeaderMode,
  isMissingCell,
  parseCellRows,
} from "@/lib/tool-kit/stats-research/matrix";

export interface DataColumn {
  /** The header cell, or "" when there is no header (the widget names it). */
  name: string;
  /** Trimmed cell text; "" for missing. */
  raw: string[];
  /** The number in each cell, or null for missing and for text. */
  numbers: (number | null)[];
  /** Every non-missing cell is a number. */
  isNumeric: boolean;
  /** Distinct non-missing cell texts, in order of first appearance. */
  levels: string[];
  missing: number;
}

export interface Dataset {
  hasHeader: boolean;
  rowCount: number;
  columns: DataColumn[];
}

function isWord(cell: string): boolean {
  return !isMissingCell(cell) && parseNumberInput(cell) === null;
}

/** First row is a header: all words, none of which recurs in its own column. */
function guessHeader(rows: readonly string[][]): boolean {
  const [first, ...rest] = rows;
  if (!first || rest.length === 0 || first.length === 0) {
    return false;
  }
  return first.every(
    (cell, j) => isWord(cell) && !rest.some((row) => row[j] === cell)
  );
}

function buildColumn(name: string, raw: string[]): DataColumn {
  const levels: string[] = [];
  const seen = new Set<string>();
  let missing = 0;
  let numeric = true;
  const numbers = raw.map((cell) => {
    if (isMissingCell(cell)) {
      missing += 1;
      return null;
    }
    if (!seen.has(cell)) {
      seen.add(cell);
      levels.push(cell);
    }
    const value = parseNumberInput(cell);
    if (value === null) {
      numeric = false;
    }
    return value;
  });
  return {
    name,
    raw: raw.map((cell) => (isMissingCell(cell) ? "" : cell)),
    numbers,
    isNumeric: numeric && levels.length > 0,
    levels,
    missing,
  };
}

function resolveHeader(mode: HeaderMode, rows: readonly string[][]): boolean {
  if (mode === "auto") {
    return guessHeader(rows);
  }
  return mode === "yes";
}

export function parseDataset(text: string, mode: HeaderMode = "auto"): Dataset {
  const { rows } = parseCellRows(text);
  const hasHeader = resolveHeader(mode, rows);
  const header = hasHeader ? (rows[0] ?? []) : [];
  const body = hasHeader ? rows.slice(1) : rows;
  const width = Math.max(header.length, ...body.map((row) => row.length), 0);
  const columns = Array.from({ length: width }, (_, j) =>
    buildColumn(
      header[j] ?? "",
      body.map((row) => row[j] ?? "")
    )
  );
  return { hasHeader, rowCount: body.length, columns };
}

/** A column's display name: its header, or a numbered default from the widget. */
export function columnName(
  column: DataColumn,
  index: number,
  fallback: (number: number) => string
): string {
  return column.name === "" ? fallback(index + 1) : column.name;
}

/**
 * Indexes of the rows where every listed column has a value, plus how many rows were
 * left out for that reason. `values` are per-column arrays of number-or-null.
 */
export function completeRows(values: readonly (readonly (number | null)[])[]): {
  rows: number[];
  dropped: number;
} {
  const total = values[0]?.length ?? 0;
  const rows: number[] = [];
  for (let i = 0; i < total; i++) {
    if (values.every((column) => column[i] !== null)) {
      rows.push(i);
    }
  }
  return { rows, dropped: total - rows.length };
}

/** Pick the given rows from a nullable column as plain numbers. */
export function take(
  column: readonly (number | null)[],
  rows: readonly number[]
): number[] {
  return rows.map((i) => column[i] as number);
}

/**
 * The level coded 1 by default. Numbers: the larger value (so 0/1 data gives 1).
 * Text: the later level alphabetically, as R's factor ordering does.
 */
export function defaultPositiveLevel(column: DataColumn): string {
  const [a, b] = column.levels;
  if (b === undefined) {
    return a ?? "";
  }
  if (column.isNumeric) {
    return (parseNumberInput(a) ?? 0) >= (parseNumberInput(b) ?? 0) ? a : b;
  }
  return a.localeCompare(b, "en") >= 0 ? a : b;
}

/**
 * A column as numbers for modelling. Numeric columns are used as they are. A text column
 * with exactly two levels becomes 0/1, with `positive` coded 1 (the *other* level is the
 * reference); anything else cannot be modelled and gives null.
 */
export function encodeColumn(
  column: DataColumn,
  positive?: string
): (number | null)[] | null {
  if (column.isNumeric) {
    return column.numbers;
  }
  if (column.levels.length !== 2) {
    return null;
  }
  const event = positive ?? defaultPositiveLevel(column);
  return column.raw.map((cell) => {
    if (cell === "") {
      return null;
    }
    return cell === event ? 1 : 0;
  });
}

/** A column usable as a predictor or response: numeric, or exactly two text levels. */
export function isModellable(column: DataColumn): boolean {
  return column.isNumeric || column.levels.length === 2;
}
